/**
 * Store Admin Dashboard
 *
 * Central hub for staff to manage:
 * - POS operations
 * - Product management
 * - Scan & Go verification
 * - Locker assignments
 * - Reports and analytics
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';

interface DashboardStats {
  today_sales: number;
  today_transactions: number;
  pending_scan_go: number;
  orders_ready_for_locker: number;
  active_sessions: number;
}

const API_BASE = '/api';

const apiCall = async (endpoint: string) => {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch');
  return response.json();
};

const StoreAdmin: React.FC = () => {
  const { isAuthenticated, user } = useStoreAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadStats();
  }, [isAuthenticated, navigate]);

  const loadStats = async () => {
    try {
      setLoading(true);
      // Load various stats
      const [scanGo, lockers] = await Promise.all([
        apiCall('/admin/scan-and-go/pending/').catch(() => ({ sessions: [] })),
        apiCall('/admin/lockers/orders-ready/').catch(() => ({ orders: [] })),
      ]);

      setStats({
        today_sales: 0,  // Would come from EOD report
        today_transactions: 0,
        pending_scan_go: scanGo.sessions?.length || 0,
        orders_ready_for_locker: lockers.orders?.length || 0,
        active_sessions: 0,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />

      <main style={styles.main}>
        <div style={styles.header}>
          <h1 style={styles.title}>Store Admin</h1>
          <p style={styles.welcome}>Welcome, {user?.username || 'Staff'}</p>
        </div>

        {/* Quick Actions Grid */}
        <div style={styles.grid}>
          {/* POS */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Point of Sale</h2>
            <div style={styles.actionGrid}>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/pos')}
              >
                <span style={styles.actionIcon}>&#128179;</span>
                <span style={styles.actionLabel}>Open POS</span>
              </button>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/scan-go-verify')}
              >
                <span style={styles.actionIcon}>&#128270;</span>
                <span style={styles.actionLabel}>Scan & Go</span>
                {stats && stats.pending_scan_go > 0 && (
                  <span style={styles.badge}>{stats.pending_scan_go}</span>
                )}
              </button>
            </div>
          </div>

          {/* Orders & Lockers */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Orders & Lockers</h2>
            <div style={styles.actionGrid}>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/admin/mill-orders')}
              >
                <span style={styles.actionIcon}>&#128230;</span>
                <span style={styles.actionLabel}>Mill Store Orders</span>
              </button>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/admin/lockers')}
              >
                <span style={styles.actionIcon}>&#128274;</span>
                <span style={styles.actionLabel}>Locker Management</span>
                {stats && stats.orders_ready_for_locker > 0 && (
                  <span style={styles.badge}>{stats.orders_ready_for_locker}</span>
                )}
              </button>
            </div>
          </div>

          {/* Products */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Products</h2>
            <div style={styles.actionGrid}>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/admin/pos/products')}
              >
                <span style={styles.actionIcon}>&#128722;</span>
                <span style={styles.actionLabel}>Product Catalog</span>
                <span style={styles.actionNote}>(Opens in Admin)</span>
              </button>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/barcode-scanner')}
              >
                <span style={styles.actionIcon}>&#128187;</span>
                <span style={styles.actionLabel}>Barcode Scanner</span>
              </button>
            </div>
          </div>

          {/* Labels & QR Codes */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Labels & QR Codes</h2>
            <div style={styles.actionGrid}>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/byoc-labels')}
              >
                <span style={styles.actionIcon}>&#127991;</span>
                <span style={styles.actionLabel}>BYOC Bin Labels</span>
              </button>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/product-qr-codes')}
              >
                <span style={styles.actionIcon}>&#128178;</span>
                <span style={styles.actionLabel}>Product QR Codes</span>
              </button>
            </div>
          </div>

          {/* Reports */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Reports</h2>
            <div style={styles.actionGrid}>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/admin/reports/eod')}
              >
                <span style={styles.actionIcon}>&#128200;</span>
                <span style={styles.actionLabel}>End of Day Reports</span>
              </button>
              <button
                style={styles.actionBtn}
                onClick={() => navigate('/store/admin/reports/sales')}
              >
                <span style={styles.actionIcon}>&#128176;</span>
                <span style={styles.actionLabel}>Sales Analytics</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div style={styles.statsSection}>
            <h2 style={styles.sectionTitle}>Today's Overview</h2>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <span style={styles.statValue}>{stats.pending_scan_go}</span>
                <span style={styles.statLabel}>Pending Scan & Go</span>
              </div>
              <div style={styles.statCard}>
                <span style={styles.statValue}>{stats.orders_ready_for_locker}</span>
                <span style={styles.statLabel}>Orders for Lockers</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  welcome: {
    color: '#666',
    margin: '0.5rem 0 0 0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 1rem 0',
  },
  actionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  actionBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '1.5rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
  },
  actionIcon: {
    fontSize: '2rem',
  },
  actionLabel: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },
  actionNote: {
    fontSize: '0.7rem',
    color: '#888',
    fontWeight: 'normal',
  },
  badge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: '#c62828',
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    padding: '0.25rem 0.5rem',
    borderRadius: '12px',
    minWidth: '20px',
    textAlign: 'center',
  },
  statsSection: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
  },
  statCard: {
    backgroundColor: '#f0f7f0',
    borderRadius: '12px',
    padding: '1.5rem',
    textAlign: 'center',
  },
  statValue: {
    display: 'block',
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
  },
  statLabel: {
    display: 'block',
    fontSize: '0.875rem',
    color: '#666',
    marginTop: '0.5rem',
  },
};

export default StoreAdmin;
